import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Briefcase, MapPin, Clock, Users, ArrowRight, DollarSign, Code, ShieldCheck, Globe, Cpu } from "lucide-react";
import jobsData from "@/data/jobs.json";
import { useCareerApplication } from "@/contexts/CareerApplicationContext";

export default function Careers() {
  const { openDrawer, setSelectedJob } = useCareerApplication();

  return (
    <>
      <SEO
        title="Careers at Kecly - Join Our Team in UAE"
        description="Explore career opportunities at Kecly. Join our team of innovative developers and digital experts in Sharjah, UAE."
        keywords="careers Kecly, jobs UAE, software developer jobs Sharjah, tech careers Dubai"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-30">
          <Cpu className="absolute top-1/4 left-1/4 w-8 h-8 text-primary/20" />
          <Code className="absolute top-1/3 right-1/3 w-8 h-8 text-primary/20" />
          <Globe className="absolute bottom-1/4 right-1/4 w-8 h-8 text-primary/20" />
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="font-orbitron font-bold text-5xl md:text-6xl gradient-text mb-6">
              Join Our Team
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
              Be part of a dynamic team that's shaping the future of technology in the UAE
            </p>

          </motion.div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-gray-100">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Why Work With Us?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We're not just another tech company - we're a team that values innovation, growth, and work-life balance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm"
              whileHover={{ scale: 1.03 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6 mx-auto">
                <Cpu className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-center mb-4">Cutting-Edge Tech</h3>
              <p className="text-gray-600 text-center">
                Work with the latest technologies and frameworks on challenging projects that push boundaries.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm"
              whileHover={{ scale: 1.03 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6 mx-auto">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-center mb-4">Great Team Culture</h3>
              <p className="text-gray-600 text-center">
                Join a supportive team that values collaboration, learning, and having fun together.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm"
              whileHover={{ scale: 1.03 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <div className="flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6 mx-auto">
                <ArrowRight className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-xl font-bold text-center mb-4">Career Growth</h3>
              <p className="text-gray-600 text-center">
                We invest in our team with training, mentorship, and opportunities to take on new challenges.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Open Positions</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Find your next opportunity with us
            </p>
          </motion.div>

          <div className="space-y-6">
            {jobsData.map((job) => (
              <motion.div
                key={job.id}
                className="bg-card rounded-xl p-6 border border-border hover:shadow-lg transition-all duration-300 relative overflow-hidden group"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-primary">{job.title}</h3>
                    <div className="flex items-center text-muted-foreground mt-2 gap-4">
                      <span className="flex items-center">
                        <MapPin className="w-4 h-4 mr-1" />
                        {job.location}
                      </span>
                      <span className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {job.type}
                      </span>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-primary border-primary">
                    {job.salary}
                  </Badge>
                </div>
                
                <p className="text-muted-foreground mb-4 line-clamp-3">{job.description}</p>
                
                <div className="flex flex-wrap gap-2 mb-6">
                  {job.skills?.map((skill) => (
                    <Badge key={skill} variant="secondary" className="flex items-center gap-1">
                      {skill === 'React' && <Code className="w-3 h-3" />}
                      {skill === 'Security' && <ShieldCheck className="w-3 h-3" />}
                      {skill}
                    </Badge>
                  ))}
                </div>
                
                <div className="flex justify-between items-center pt-4 border-t border-border">
                  <Button 
                    variant="outline"
                    onClick={() => openDrawer(job.title)}
                    className="group"
                  >
                    Quick Apply
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    className="text-primary hover:bg-primary/10"
                    onClick={() => setSelectedJob(job)}
                  >
                    View Details
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          {jobsData.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              className="text-center py-12"
            >
              <h3 className="font-orbitron font-semibold text-xl mb-4">No Open Positions</h3>
              <p className="text-muted-foreground mb-6">
                We're not actively hiring right now, but we're always interested in meeting talented individuals.
              </p>
              <Button className="btn-secondary">Send Your Resume</Button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Benefits</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We offer a range of benefits to support your well-being and career growth
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center text-center">
              <DollarSign className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Competitive Salary</h3>
              <p className="text-muted-foreground">We offer industry-leading compensation packages</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <Globe className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Remote Options</h3>
              <p className="text-muted-foreground">Work from anywhere with our flexible policies</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <ShieldCheck className="w-8 h-8 text-primary mb-4" />
              <h3 className="text-xl font-semibold mb-2">Health Benefits</h3>
              <p className="text-muted-foreground">Comprehensive medical and dental coverage</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-orbitron font-bold text-4xl gradient-text mb-6">Application Process</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Our streamlined hiring process is designed to be efficient and transparent
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Apply", description: "Submit your application and resume through our portal" },
              { step: "02", title: "Screen", description: "Initial screening call to discuss your background and interests" },
              { step: "03", title: "Interview", description: "Technical and cultural fit interviews with the team" },
              { step: "04", title: "Offer", description: "Receive an offer and join the Kecly family" },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="font-orbitron font-bold text-primary-foreground">{item.step}</span>
                </div>
                <h3 className="font-orbitron font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
